import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  requestAdminRegisterOtp,
  verifyAdminRegisterOtp,
  adminRegister,
  type AdminRegisterPayload,
} from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { useCooldown } from '@shared/hooks/use-cooldown';
import { validateEmail } from '@shared/utils/validation';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { ME_QUERY_KEY } from '@/hooks/use-me';
import type { AdminRole } from '@shared/types/api';
import { EyeIcon, EyeOffIcon } from '@/components/layout/icons';
import { AuthCard } from './auth-card';

const OPERABLE_ADMIN_ROLES: { value: AdminRole; label: string }[] = [
  { value: 'OH_SERVICE_MANAGER', label: 'OH / Service Manager' },
  { value: 'ASSIGNER', label: 'Assigner' },
  { value: 'CASE_EXECUTIVE', label: 'Case Executive' },
  { value: 'ASSOCIATE_CONTROLLING_EXECUTIVE', label: 'Associate Controlling Executive' },
  { value: 'CUSTOMER_SUPPORT_EXECUTIVE', label: 'Customer Support Executive' },
  { value: 'FINANCE_EXECUTIVE', label: 'Finance Executive' },
  { value: 'AUDITOR_COMPLIANCE_REVIEWER', label: 'Auditor / Compliance Reviewer' },
];

export function RegisterPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [adminRole, setAdminRole] = useState<AdminRole>('OH_SERVICE_MANAGER');
  const [otpSent, setOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [signupToken, setSignupToken] = useState<string | null>(null);

  const { secondsLeft, active: onCooldown, start: startCooldown } = useCooldown(30);

  const sendOtpMutation = useMutation({
    mutationFn: (e: string) => requestAdminRegisterOtp(e),
    onSuccess: () => {
      setOtpSent(true);
      startCooldown();
      toast.success('6-digit OTP code sent to your email.');
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err));
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: () => verifyAdminRegisterOtp(email.trim().toLowerCase(), otpCode.trim()),
    onSuccess: (data) => {
      setSignupToken(data.signupToken);
      setEmailVerified(true);
      toast.success('Email verified successfully! You can now set your password and role.');
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err));
    },
  });

  const registerMutation = useMutation({
    mutationFn: (payload: AdminRegisterPayload) => adminRegister(payload),
    onSuccess: (data) => {
      getTokenStore().setAccessToken(data.accessToken, 'admin');
      setSession(data.user);
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      toast.success('Registration submitted! Account awaiting Super Admin verification.');
      navigate('/', { replace: true });
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err));
    },
  });

  function handleSendOtp() {
    setEmailTouched(true);
    const result = validateEmail(email);
    if (!result.isValid) {
      setEmailError(result.error);
      toast.error(result.error);
      return;
    }
    setEmailError(null);
    sendOtpMutation.mutate(email.trim().toLowerCase());
  }

  function handleEmailChange(val: string) {
    setEmail(val);
    if (emailTouched) {
      const result = validateEmail(val);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  function handleEmailBlur() {
    setEmailTouched(true);
    if (email.trim()) {
      const result = validateEmail(email);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  function handleVerifyOtp() {
    if (!otpCode.trim() || otpCode.trim().length !== 6) {
      toast.error('Please enter the 6-digit OTP code.');
      return;
    }
    verifyOtpMutation.mutate();
  }

  function handleChangeEmail() {
    setEmailVerified(false);
    setSignupToken(null);
    setOtpSent(false);
    setOtpCode('');
    setEmailError(null);
    setEmailTouched(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!emailVerified || !signupToken) {
      toast.error('Please verify your work email with OTP first.');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter your first and last name.');
      return;
    }
    if (!mobile.trim() || mobile.trim().length < 10) {
      toast.error('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    registerMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      mobile: mobile.trim(),
      email: email.trim().toLowerCase(),
      signupToken,
      password,
      adminRole,
    });
  }

  return (
    <AuthCard>
      <div className="space-y-5">
        <div className="text-center">
          <h1 className="text-lg font-bold text-gray-900">Admin Registration</h1>
          <p className="mt-1 text-xs text-gray-500">
            Apply for administrative access. Accounts are verified by Super Admin.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Mobile number */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <Input
              type="tel"
              placeholder="9876543210"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
              required
            />
          </div>

          {/* Email & Send OTP */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[11px] font-semibold text-gray-700">
                Work Email <span className="text-red-500">*</span>
              </label>
              {emailVerified && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Verified
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="operator@ecregi.com"
                value={email}
                disabled={emailVerified}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                required
                className={`flex-1 ${emailVerified ? 'bg-gray-100 cursor-not-allowed opacity-90' : ''} ${emailError && !emailVerified ? 'border-red-500 focus:border-red-600 focus:ring-red-500/30' : ''}`}
              />
              {!emailVerified ? (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sendOtpMutation.isPending || onCooldown || !email}
                  className="rounded-lg bg-brand-800 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-900 disabled:opacity-50 whitespace-nowrap transition-colors"
                >
                  {sendOtpMutation.isPending
                    ? 'Sending…'
                    : onCooldown
                    ? `${secondsLeft}s`
                    : otpSent
                    ? 'Resend OTP'
                    : 'Send OTP'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleChangeEmail}
                  className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 whitespace-nowrap transition-colors"
                >
                  Change
                </button>
              )}
            </div>
            {emailError && !emailVerified && (
              <p className="mt-1 text-[11px] font-medium text-red-600">{emailError}</p>
            )}
            {otpSent && !emailVerified && (
              <p className="mt-1 text-[11px] text-teal-600">
                ✓ OTP sent! Check your inbox or database.
              </p>
            )}
          </div>

          {/* OTP Code & Verify Button (Shown only if OTP sent and not yet verified) */}
          {otpSent && !emailVerified && (
            <div className="rounded-xl border border-brand-100 bg-brand-50/50 p-3 space-y-2">
              <label className="block text-[11px] font-semibold text-brand-900">
                Enter 6-Digit OTP <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                  className="flex-1 bg-white font-mono tracking-widest text-center"
                />
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={verifyOtpMutation.isPending || otpCode.trim().length !== 6}
                  className="rounded-lg bg-teal-700 px-3.5 py-2 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-50 whitespace-nowrap transition-colors"
                >
                  {verifyOtpMutation.isPending ? 'Verifying…' : 'Verify OTP'}
                </button>
              </div>
            </div>
          )}

          {/* Step Guidance Notice (When email is not verified yet) */}
          {!emailVerified && (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/70 p-3 text-center">
              <p className="text-[11px] text-gray-500 leading-normal">
                Please enter your email and verify OTP to set your password and select your admin role.
              </p>
            </div>
          )}

          {/* PASSWORD, ADMIN ROLE & SUBMIT FIELDS (ONLY SHOWN AFTER EMAIL OTP IS VERIFIED) */}
          {emailVerified && (
            <>
              {/* Password fields */}
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min 8 chars"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-hidden p-1 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      tabIndex={-1}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-hidden p-1 transition-colors"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Admin Role Selector Dropdown */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  Requested Admin Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={adminRole}
                  onChange={(e) => setAdminRole(e.target.value as AdminRole)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-900 focus:border-brand-600 focus:outline-hidden focus:ring-1 focus:ring-brand-600"
                  required
                >
                  {OPERABLE_ADMIN_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[10px] text-gray-500">
                  Note: Super Admin role is managed separately via system bootstrap.
                </p>
              </div>

              {/* Submit button */}
              <Button
                type="submit"
                block
                disabled={registerMutation.isPending}
                className="mt-2"
              >
                {registerMutation.isPending ? 'Submitting Application…' : 'Register Admin Account'}
              </Button>
            </>
          )}

          <div className="text-center pt-2">
            <Link
              to="/login"
              className="text-xs font-semibold text-brand-700 hover:text-brand-800"
            >
              Already have an admin account? Sign in
            </Link>
          </div>
        </form>
      </div>
    </AuthCard>
  );
}
